import * as CrisprConstants from 'constants/CRISPRConstants';
import preferences from 'Preferences';

const {DEFAULTS} = CrisprConstants;

/**
 * This type is very similar to CRISPRConstants.CRISPRConstantDefaults, but omits GENOME_PREFERENCES, and
 * changes all the names to camelCase, as is the convention in Preferences.
 */
export interface CRISPRUserSettings {
  /** The preferred genome to design guides against */
  genome: string;
  /** The preferred PAM (protospacer adjacent motif) to follow the CRISPR target */
  pam: CrisprConstants.PAM;
  /** Whether the preferred PAM should instead precede the CRISPR target */
  isLeftPAM: boolean;
  /** Whether to default to designing single guides, paired guides, or base editors */
  designType: CrisprConstants.DESIGN_TYPE;
  /** The preferred length for guides */
  guideLength: number;
  /**
   * For paired guides, the preferred minimum number of base pairs between the start of the forward guide and
   * the end of the reverse guide
   */
  pairOffsetMin: number;
  /**
   * For paired guides, the preferred maximum number of base pairs between the start of the forward guide and
   * the end of the reverse guide
   */
  pairOffsetMax: number;
  /** The preferred minimum number of A bases in a guide */
  aMin: number;
  /** The preferred maximum number of A bases in a guide */
  aMax: number;
  /** The preferred minimum number of T bases in a guide */
  tMin: number;
  /** The preferred maximum number of T bases in a guide */
  tMax: number;
  /** The preferred minimum number of C bases in a guide */
  cMin: number;
  /** The preferred maximum number of C bases in a guide */
  cMax: number;
  /** The preferred minimum number of G bases in a guide */
  gMin: number;
  /** The preferred maximum number of G bases in a guide */
  gMax: number;
  /** The preferred minimum on-target score for a PAM site */
  pamSiteOnTargetMin: number;
  /** The preferred maximum on-target score for a PAM site */
  pamSiteOffTargetMin: number;
  /** Whether to default to showing cut sites outside the specified genome region */
  showCutsOutsideRegions: boolean;
  /** Whether to default to searching over soft masked bases in the genome */
  searchSoftMaskedBases: number;
  /** Which off-target score algorithm to default to */
  offTargetScoreType: CrisprConstants.OFF_TARGET_SCORE_TYPE;
  /** Whether to default to the Azimuth model for on-target scoring (vs Doench) */
  useAzimuthOnTargetScore: boolean;
}

const CRISPR_DEFAULTS_KEYS: {[name: string]: keyof CRISPRUserSettings} = {
  SHOW_CUTS_OUTSIDE_REGIONS: 'showCutsOutsideRegions',
  SEARCH_SOFT_MASKED_BASES: 'searchSoftMaskedBases',
  GENOME: 'genome',
  OFF_TARGET_SCORE_TYPE: 'offTargetScoreType',
  USE_AZIMUTH_ON_TARGET_SCORE: 'useAzimuthOnTargetScore',
};

const getKey = (key: keyof CRISPRUserSettings): string => `crispr_defaults.${key}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const get = (key: keyof CRISPRUserSettings): any => {
  const existingValue = preferences.get(CRISPRDefaults.getKey(key));
  if (existingValue != null) {
    return existingValue;
  }

  switch (key) {
    case CRISPR_DEFAULTS_KEYS.GENOME:
      return DEFAULTS.GENOME_ENSEMBL_NAME;
    case 'pam':
      return DEFAULTS.PAM;
    case 'isLeftPAM':
      return DEFAULTS.IS_LEFT_PAM;
    case 'designType':
      return DEFAULTS.DESIGN_TYPE;
    case 'guideLength':
      return DEFAULTS.GUIDE_LENGTH;
    case 'pairOffsetMin':
      return DEFAULTS.PAIR_OFFSET_MIN;
    case 'pairOffsetMax':
      return DEFAULTS.PAIR_OFFSET_MAX;
    case 'aMin':
      return DEFAULTS.A_MIN;
    case 'aMax':
      return DEFAULTS.A_MAX;
    case 'tMin':
      return DEFAULTS.T_MIN;
    case 'tMax':
      return DEFAULTS.T_MAX;
    case 'cMin':
      return DEFAULTS.C_MIN;
    case 'cMax':
      return DEFAULTS.C_MAX;
    case 'gMin':
      return DEFAULTS.G_MIN;
    case 'gMax':
      return DEFAULTS.G_MAX;
    case 'pamSiteOnTargetMin':
      return DEFAULTS.PAM_SITE_ON_TARGET_MIN;
    case 'pamSiteOffTargetMin':
      return DEFAULTS.PAM_SITE_OFF_TARGET_MIN;
    case CRISPR_DEFAULTS_KEYS.SHOW_CUTS_OUTSIDE_REGIONS:
      return DEFAULTS.SHOW_CUTS_OUTSIDE_REGIONS;
    case CRISPR_DEFAULTS_KEYS.SEARCH_SOFT_MASKED_BASES:
      return DEFAULTS.SEARCH_SOFT_MASKED_BASES;
    case CRISPR_DEFAULTS_KEYS.OFF_TARGET_SCORE_TYPE:
      return DEFAULTS.OFF_TARGET_SCORE_TYPE;
    case CRISPR_DEFAULTS_KEYS.USE_AZIMUTH_ON_TARGET_SCORE:
      return DEFAULTS.USE_AZIMUTH_ON_TARGET_SCORE;
    default:
      throw new Error(`Unexpected key: ${key}`);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const set = (key: keyof CRISPRUserSettings, value: any): void => {
  preferences.set(CRISPRDefaults.getKey(key), value);
};

const getDefaults = (): CRISPRUserSettings => ({
  genome: CRISPRDefaults.get(CRISPR_DEFAULTS_KEYS.GENOME),
  pam: CRISPRDefaults.get('pam'),
  isLeftPAM: CRISPRDefaults.get('isLeftPAM'),
  designType: CRISPRDefaults.get('designType'),
  guideLength: CRISPRDefaults.get('guideLength'),
  pairOffsetMin: CRISPRDefaults.get('pairOffsetMin'),
  pairOffsetMax: CRISPRDefaults.get('pairOffsetMax'),
  aMin: CRISPRDefaults.get('aMin'),
  aMax: CRISPRDefaults.get('aMax'),
  tMin: CRISPRDefaults.get('tMin'),
  tMax: CRISPRDefaults.get('tMax'),
  cMin: CRISPRDefaults.get('cMin'),
  cMax: CRISPRDefaults.get('cMax'),
  gMin: CRISPRDefaults.get('gMin'),
  gMax: CRISPRDefaults.get('gMax'),
  pamSiteOnTargetMin: CRISPRDefaults.get('pamSiteOnTargetMin'),
  pamSiteOffTargetMin: CRISPRDefaults.get('pamSiteOffTargetMin'),
  showCutsOutsideRegions: CRISPRDefaults.get(CRISPR_DEFAULTS_KEYS.SHOW_CUTS_OUTSIDE_REGIONS),
  searchSoftMaskedBases: CRISPRDefaults.get(CRISPR_DEFAULTS_KEYS.SEARCH_SOFT_MASKED_BASES),
  offTargetScoreType: CRISPRDefaults.get(CRISPR_DEFAULTS_KEYS.OFF_TARGET_SCORE_TYPE),
  useAzimuthOnTargetScore: CRISPRDefaults.get(CRISPR_DEFAULTS_KEYS.USE_AZIMUTH_ON_TARGET_SCORE),
});

const CRISPRDefaults: {
  KEYS: {[key: string]: keyof CRISPRUserSettings};
  getKey: (key: keyof CRISPRUserSettings) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (key: keyof CRISPRUserSettings) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set: (key: keyof CRISPRUserSettings, value: any) => void;
  getDefaults: () => CRISPRUserSettings;
} = {
  KEYS: CRISPR_DEFAULTS_KEYS,
  getKey,
  get,
  set,
  getDefaults,
};

export default CRISPRDefaults;
